import { useMemo } from "react";
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import { BaseWidget } from "./BaseWidget";
import { ColorControl } from "../controls/ColorControl";
import useSettings from "../../../../hooks/Settings";
import { useAddressStatusCountsState } from "../../../../hooks/useAddressStatusCounts";
import buildStatusChartData from "../../../../helpers/adressHelpers/buildStatusChartData";
import HouseVisitsStatusVisualization from "../../../dataVisualizations/HouseVisitsStatusVisualization";
import ModulePlaceholder from "./_shared/ModulePlaceholder";
import isTemplateMode from "./_shared/isTemplateMode";

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// HouseVisitsStatusWidget — bar chart of address-status counts for the
// Huishoudens module. Matches DashboardHouseVisitsStatusCard except bars
// are inert (no deep-link click) — published reports are static.
//
// Per-status colour overrides via NestedItemControl are intentionally
// deferred to a follow-up so this widget ships on parity with the
// dashboard (single bar colour) in T6.

/**
 * @extends {BaseWidget}
 */
export class HouseVisitsStatusWidget extends BaseWidget {

    getName() { return 'Status'; }
    getCategory() { return 'huishoudens'; }
    getRequiredModule() { return 'housevisits'; }
    getIcon() { return <BarChartOutlinedIcon />; }

    getControls() {
        return {
            'graphColor': new ColorControl({
                label: 'Staafkleur',
                description: 'Kleur van de staven. Leeg = gebruik de tenant-secundaire kleur.',
                defaultValue: '',
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @param {object} props
 * @param {string} [props.graphColor]
 * @returns {JSX.Element}
 */
const Renderer = ({ graphColor }) => {
    const { housevisits, SecundairyColor } = useSettings();
    const isTemplate = isTemplateMode();
    const { loaded, counts: statusCounts } = useAddressStatusCountsState();

    const data = useMemo(() => buildStatusChartData(statusCounts), [statusCounts]);

    if (isTemplate) {
        return <ModulePlaceholder mode="template" />;
    }
    if (housevisits !== true) {
        return <ModulePlaceholder mode="disabled" moduleLabel="Huishoudens" />;
    }

    // Geen staven van 0 zolang de huishoudens laden: dat leest als 'niemand'.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    if (!loaded) {
        return <p className="score-explain-card__placeholder">Laden…</p>;
    }

    const color = graphColor || SecundairyColor;

    return (
        <div className="housevisits-widget-chart">
            <HouseVisitsStatusVisualization
                data={data}
                color={color}
                height={'100%'}
            />
        </div>
    );
};
